import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Copilot from "@/components/Copilot";
import {
  LayoutDashboard, KanbanSquare, Radar, ReceiptText, Contact2,
  Building2, ShieldCheck, LogOut, Menu, X, Sun, Moon, Landmark, Store, FolderOpen, Ruler, Database, Settings as SettingsIcon, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Command", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
  { to: "/proposals/federal", label: "Federal Proposals", icon: Landmark, testid: "nav-federal" },
  { to: "/proposals/commercial", label: "Commercial Proposals", icon: Store, testid: "nav-commercial" },
  { to: "/discover", label: "Discover", icon: Radar, testid: "nav-discover" },
  { to: "/takeoff", label: "Takeoff", icon: Ruler, testid: "nav-takeoff" },
  { to: "/library", label: "Data Library", icon: Database, testid: "nav-library" },
  { to: "/documents", label: "Forms & AI Fill", icon: FolderOpen, testid: "nav-documents" },
  { to: "/pos", label: "Purchase Orders", icon: ReceiptText, testid: "nav-pos" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("ironclad-sidebar-collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((v) => {
    const nv = !v;
    try { localStorage.setItem("ironclad-sidebar-collapsed", nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });
  const [newMatches, setNewMatches] = useState(0);
  const [mqBids, setMqBids] = useState(0);   // Dir 170h — grounding-health: # bids with missing-quantity lines
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("ironclad-theme") || "light"; } catch { return "light"; }
  });
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark"); else root.classList.remove("dark");
    try { localStorage.setItem("ironclad-theme", theme); } catch { /* ignore */ }
  }, [theme]);

  // Per-user "new match" badge on Discover: count opportunities ingested since this user last looked.
  useEffect(() => {
    if (!user) return;
    const fetchCount = async () => {
      try { const r = await api.get("/opportunities/new-count"); setNewMatches(r.data.count || 0); }
      catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 90000);
    const clear = () => setNewMatches(0);
    window.addEventListener("ironclad:discover-seen", clear);
    return () => { clearInterval(id); window.removeEventListener("ironclad:discover-seen", clear); };
  }, [user]);

  // Dir 170h — grounding-health badge: poll the latest retro-sweep for bids with MISSING-QUANTITY lines.
  useEffect(() => {
    if (!user) return;
    const fetchMq = async () => {
      try { const r = await api.get("/pricing/line-grounding/sweep/latest"); setMqBids(r.data?.missing_quantity_bids || 0); }
      catch { /* non-admin or none */ }
    };
    fetchMq();
    const id = setInterval(fetchMq, 120000);
    const onUpd = (e) => setMqBids(e?.detail?.mq || 0);
    window.addEventListener("ironclad:grounding-sweep-updated", onUpd);
    return () => { clearInterval(id); window.removeEventListener("ironclad:grounding-sweep-updated", onUpd); };
  }, [user]);
  const nav = [...NAV];
  // Dir 65 — roles flattened: every authenticated employee sees Settings and Admin & Users.
  if (user) {
    nav.push({ to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" });
    nav.push({ to: "/admin", label: "Admin & Users", icon: ShieldCheck, testid: "nav-admin" });
  }

  const go = (to) => { navigate(to); setOpen(false); };
  const goToSweep = () => { navigate({ pathname: "/admin", hash: "#grounding-sweep" }); setOpen(false); };

  const renderSidebar = (isCollapsed, showToggle) => (
    <aside className={`${isCollapsed ? "w-16" : "w-64"} shrink-0 bg-zinc-950 text-zinc-300 flex flex-col h-full border-r border-zinc-900 transition-[width] duration-200`}>
      <div className={`py-5 border-b border-zinc-800 flex items-center ${isCollapsed ? "px-2 justify-center" : "px-5 gap-3"}`}>
        <img src="/wd_brand_logo.png" alt="Williams Diversified" data-testid="sidebar-brand-logo" className="h-10 w-10 object-contain shrink-0" />
        {!isCollapsed && (
          <div className="leading-tight flex-1 min-w-0">
            <div className="font-display font-bold text-xl tracking-tight text-white">Ironclad</div>
            <div className="text-[10px] tracking-[0.12em] text-zinc-400">by Williams Diversified</div>
          </div>
        )}
        {showToggle && !isCollapsed && (
          <button data-testid="sidebar-collapse-btn" onClick={toggleCollapsed} title="Collapse sidebar"
            className="shrink-0 text-zinc-400 hover:text-white p-1 rounded hover:bg-zinc-900"><PanelLeftClose className="h-4 w-4" /></button>
        )}
      </div>
      {showToggle && isCollapsed && (
        <button data-testid="sidebar-expand-btn" onClick={toggleCollapsed} title="Expand sidebar"
          className="mx-auto mt-2 text-zinc-400 hover:text-white p-1.5 rounded hover:bg-zinc-900"><PanelLeftOpen className="h-4 w-4" /></button>
      )}
      <nav className={`flex-1 py-4 space-y-1 overflow-y-auto overflow-x-hidden ${isCollapsed ? "px-2" : "px-3"}`}>
        {nav.map((item) => {
          const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
          const Icon = item.icon;
          return (
            <button key={item.to} data-testid={item.testid} onClick={() => go(item.to)} title={isCollapsed ? item.label : undefined}
              className={`w-full flex items-center rounded-md transition-colors text-left ${isCollapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"} text-[15px] ${
                active ? "bg-zinc-800 text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"}`}>
              <span className="relative shrink-0">
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                {isCollapsed && item.to === "/discover" && newMatches > 0 && (
                  <span data-testid="discover-new-badge-collapsed"
                    className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-gold text-zinc-950 text-[9px] font-bold flex items-center justify-center">
                    {newMatches > 9 ? "9+" : newMatches}
                  </span>
                )}
                {isCollapsed && item.to === "/admin" && mqBids > 0 && (
                  <span data-testid="grounding-health-badge-collapsed" onClick={(e) => { e.stopPropagation(); goToSweep(); }}
                    title={`${mqBids} bid(s) with missing-quantity pricing`}
                    className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-zinc-950">
                    {mqBids > 9 ? "9+" : mqBids}
                  </span>
                )}
              </span>
              {!isCollapsed && <span className="truncate min-w-0">{item.label}</span>}
              {!isCollapsed && item.to === "/discover" && newMatches > 0 && (
                <span data-testid="discover-new-badge"
                  className="ml-auto shrink-0 min-w-[20px] h-[20px] px-1.5 rounded-full bg-gold text-zinc-950 text-[10px] font-bold flex items-center justify-center">
                  {newMatches > 99 ? "99+" : newMatches}
                </span>
              )}
              {!isCollapsed && item.to === "/admin" && mqBids > 0 && (
                <span data-testid="grounding-health-badge" onClick={(e) => { e.stopPropagation(); goToSweep(); }}
                  title={`${mqBids} bid(s) with missing-quantity pricing — click to review`}
                  className="ml-auto shrink-0 min-w-[20px] h-[20px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center hover:bg-amber-400 transition-colors">
                  {mqBids > 99 ? "99+" : mqBids}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className={`py-4 border-t border-zinc-800 ${isCollapsed ? "px-2" : "px-3"}`}>
        {isCollapsed ? (
          <button data-testid="theme-toggle-collapsed" onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            title="Toggle theme"
            className="w-full flex items-center justify-center py-2 text-zinc-400 hover:text-white rounded hover:bg-zinc-900">
            {theme === "light" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        ) : (
          <div className="flex items-center bg-zinc-900 rounded-md p-1 mb-3" data-testid="theme-toggle">
            <button data-testid="theme-light-btn" onClick={() => setTheme("light")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs rounded transition-colors ${
                theme === "light" ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-zinc-200"}`}>
              <Sun className="h-3.5 w-3.5" /> Light
            </button>
            <button data-testid="theme-dark-btn" onClick={() => setTheme("dark")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs rounded transition-colors ${
                theme === "dark" ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-zinc-200"}`}>
              <Moon className="h-3.5 w-3.5" /> Dark
            </button>
          </div>
        )}
        {!isCollapsed && (
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-md object-cover border border-zinc-700" />
              : <div className="h-8 w-8 rounded-md bg-gold/90 flex items-center justify-center text-xs font-bold text-zinc-950">{user?.initials || user?.name?.[0]}</div>}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-zinc-200 truncate">{user?.name}</div>
              <div className="text-[10px] tracking-wide text-zinc-400 uppercase">{user?.role}</div>
            </div>
          </div>
        )}
        <button data-testid="logout-button" onClick={logout} title={isCollapsed ? "Sign out" : undefined}
          className={`w-full mt-1 flex items-center text-sm text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200 rounded-md transition-colors ${isCollapsed ? "justify-center py-2" : "gap-3 px-3 py-2"}`}>
          <LogOut className="h-4 w-4" strokeWidth={1.75} /> {!isCollapsed && "Sign out"}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar (collapsible) */}
      <div className="hidden md:block fixed h-screen z-30">{renderSidebar(collapsed, true)}</div>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-zinc-950 border-b border-zinc-800 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src="/wd_brand_logo.png" alt="" className="h-8 w-8 object-contain" />
          <span className="font-display font-bold text-white">Ironclad</span>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)}><Menu className="h-6 w-6 text-zinc-300" /></button>
      </div>

      {/* Mobile drawer (always full width) */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="h-full" onClick={(e) => e.stopPropagation()}>{renderSidebar(false, false)}</div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)}>
            <button className="absolute top-4 right-4" data-testid="mobile-close-btn"><X className="h-6 w-6 text-white" /></button>
          </div>
        </div>
      )}

      <main className={`flex-1 min-w-0 overflow-x-hidden ${collapsed ? "md:ml-16" : "md:ml-64"} min-h-screen pt-14 md:pt-0 transition-[padding,margin] duration-200 ${copilotOpen ? "lg:pr-[420px]" : ""}`}>{children}</main>
      <Copilot open={copilotOpen} setOpen={setCopilotOpen} />
    </div>
  );
}
